import { Logger } from '@/Logger';

import { TeoEventTarget } from '../utility/TeoEventTarget';

const LOG = Logger('TeoStore');

// type ExtractNonFunctionKeys<T extends object> = {
//   [K in keyof T]: 'events' extends K ? never
//   : // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
//   T[K] extends Function ? never
//   : K extends 'events' ? never
//   : K & string;
// }[keyof T];

// TODO@imre can not use Snapshot because of sveltes typescript errors
// export type Snapshot<T> = {
//   readonly [K in keyof T]: Snapshot<T[K]>;
// };
export type Snapshot<T> = T;

type IfEquals<X, Y, True = X, False = never> =
  (<G>() => G extends X ? 1 : 2) extends <G>() => G extends Y ? 1 : 2 ? True : False;

type WritableKeysOf<T> = {
  [P in keyof T]-?: IfEquals<{ [Q in P]: T[P] }, { -readonly [Q in P]: T[P] }, P, never>;
}[keyof T];

export type ExtractWritablePropertyKeys<T extends StateObject> = Extract<
  {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof T]: T[K] extends (...args: any[]) => any ? never : K & string;
  }[keyof T],
  WritableKeysOf<T> & string
>;

export type ExtractStateEvents<T extends StateObject> = {
  [K in ExtractWritablePropertyKeys<T>]: (value: T[K]) => void;
};

export type StateEvents<T extends StateObject> = ExtractStateEvents<T> & {
  '*': (state: Snapshot<T>) => void;
};

export type StateObject = object; // Record<string, unknown>;

export class TeoStore<TState extends StateObject> {
  __debug = false;
  protected _state: TState;
  private _snapshot: Snapshot<TState>;

  protected readonly events: TeoEventTarget<StateEvents<TState>>;

  protected equals(_prop: keyof TState, a: unknown, b: unknown) {
    return Object.is(a, b);
  }

  constructor(initialState: TState) {
    // super();
    this.events = new TeoEventTarget<StateEvents<TState>>();
    this._state = initialState;
    this._snapshot = this.snapshot();
  }

  addEventListener: TeoEventTarget<StateEvents<TState>>['addEventListener'] = (type, listener) => {
    return this.events.addEventListener(type, listener);
  };
  addEventListeners: TeoEventTarget<StateEvents<TState>>['addEventListeners'] = (listeners) => {
    return this.events.addEventListeners(listeners);
  };

  get state(): Snapshot<TState> {
    return this._snapshot;
  }

  setStateValue<K extends ExtractWritablePropertyKeys<TState>>(
    prop: K,
    value: TState[K],
    force = false,
  ) {
    LOG.dev('@.setStateValue', prop, value, this._state[prop]);
    if (!force && this.equals(prop, this._state[prop], value)) {
      return false;
    }
    this._state[prop] = value;
    this._snapshot = this.snapshot();
    this.events.triggerEvent('*', this._snapshot);
    this.events.triggerEvent(prop as K & string, value);
    return true;
  }

  updateStateValue<K extends ExtractWritablePropertyKeys<TState>>(
    prop: K,
    setter: (current: Snapshot<TState[K]>) => TState[K],
    force = false,
  ) {
    const current = this._snapshot[prop];
    return this.setStateValue(prop, setter(current), force);
  }

  /**
   * Update the state with a callback function that returns a partial of the state. (used by svelte)
   * @param cb - The callback function that returns a partial of the state.
   */
  update(cb: (state: Snapshot<TState>) => Partial<TState>) {
    this._set(cb(this._snapshot));
  }

  /**
   * interface for svelte to be a readable store
   */
  subscribe(callback: (state: Snapshot<TState>) => void) {
    callback(this._snapshot);
    return this.addEventListener('*', () => callback(this._snapshot));
  }

  protected notifyChanged(prop: ExtractWritablePropertyKeys<TState>) {
    this.events.triggerEvent(
      prop as ExtractWritablePropertyKeys<TState> & string,
      this._state[prop],
    );
    this.events.triggerEvent('*', this._state);
  }

  /**
   * can be aliased to `set` to make it a svelte writable store
   */
  private _set(newState: Partial<TState>) {
    const changed: (keyof TState)[] = [];
    const keys = Object.keys(newState) as (keyof TState)[];
    for (const key of keys) {
      if (key) {
        const newValue = newState[key] as TState[keyof TState];
        if (!this.equals(key, this._state[key], newValue)) {
          changed.push(key);
        }

        this._state[key] = newValue;
      }
    }

    LOG.dev('@._set TRIGGER', changed);
    if (changed.length > 0) {
      this._snapshot = this.snapshot();
      this.events.triggerEvent('*', this._snapshot);
      for (const key of changed) {
        this.events.triggerEvent(
          key as ExtractWritablePropertyKeys<TState> & string,
          this._state[key] as TState[ExtractWritablePropertyKeys<TState>],
        );
      }
    }
  }

  private snapshot() {
    const snap = Object.create(
      Object.getPrototypeOf(this._state),
      Object.getOwnPropertyDescriptors(this._state),
    );
    // const snap = structuredClone(this._state);

    if (import.meta.env.DEV) Object.freeze(snap);
    // this._snapshot = snap;
    return snap;
  }
}

export class TeoWritableStore<T extends StateObject> extends TeoStore<T> {
  /**
   * aliased `_set` to make it a svelte writable store
   */
  set(newState: Partial<T>) {
    return this.update(() => newState);
  }

  /**
   * Reinitialize the store.
   * Contrary to "set" / "update", this resets the store to be equal to the new initial state
   * by updating values to the new initial state and removing values,
   * which are not in the new initial state.
   */
  reinit(newInitialState: T) {
    this.update((original) => {
      const origKeys = Object.keys(original) as (keyof T)[];
      const newKeys = Object.keys(newInitialState) as (keyof T)[];
      for (const key of origKeys) {
        if (!newKeys.includes(key)) {
          if (this.__debug) {
            console.log('@.resetTo: deleting key ', key);
          }
          newInitialState[key] = undefined as T[keyof T];
        }
      }
      return newInitialState;
    });
  }
}

export abstract class TeoPersistableStore<
  TState extends StateObject,
  TStoredState extends StateObject = TState,
> extends TeoStore<TState> {
  #fromJSON: (json: TStoredState) => TState;
  constructor(initialState: TState);
  constructor(
    initialState: TStoredState,
    fromStoredState: (json: TStoredState) => TState,
    debug?: boolean,
  );
  constructor(
    initialState: TStoredState | TState,
    fromStoredState?: (json: TStoredState) => TState,
  ) {
    const fst = fromStoredState ?? ((json: TStoredState) => json as unknown as TState);
    super(fst(initialState as TStoredState));
    this.#fromJSON = fst;
  }

  updateFromStoredState(storedState: TStoredState) {
    this.update(() => this.#fromJSON(storedState));
  }

  toJSON(): TStoredState {
    // return structuredClone(this.toStoredState());
    // oder
    return JSON.parse(JSON.stringify(this.toStorableState()));
  }

  abstract toStorableState(): TStoredState;
}

export abstract class TeoPersistableWritableStore<
  TState extends StateObject,
  TStoredState extends StateObject = TState,
> extends TeoPersistableStore<TState, TStoredState> {
  /**
   * aliased `_set` to make it a svelte writable store
   */
  set(newState: Partial<TState>) {
    return this.update(() => newState);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ProxyStateType<T extends TeoStore<any>> =
  T extends TeoStore<infer U> ? U & { subscribe: (cb: (state: U) => void) => () => void } & T
  : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function storeLens<TStore extends TeoStore<any>, TValue>(
  store: TStore,
  getter: (state: TStore['state']) => TValue,
  setter: (store: TStore, value: TValue) => void,
) {
  return {
    subscribe: (cb: (state: TValue) => void) => {
      cb(getter(store.state));
      return store.addEventListener('*', () => cb(getter(store.state)));
    },

    set: (value: TValue) => {
      setter(store.state, value);
    },
    update: (updater: (value: TValue) => TValue) => {
      const current = getter(store.state);
      const newValue = updater(current);
      setter(store.state, newValue);
    },
  };
}
